import React from 'react';
import PropTypes from 'prop-types';
import MenuIcon from '@mui/icons-material/Menu';
import {styled} from '@mui/material/styles';
import {makeStyles} from 'tss-react/mui';
import { AppBar, IconButton, Toolbar } from '@mui/material';
import {NavLink} from 'react-router-dom';
import routes from '../../../config/routes';
import Logo from '../../Logo/Logo';
import Loading from "../Feedback/Loading/Loading"
import LanguageIcon from '@mui/icons-material/Translate';
import Button from '@mui/material/Button';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import NoSsr from "@mui/material/NoSsr"
import {LANGUAGES_LABELS} from "../../../config/config"

import {useTranslation} from "react-i18next"


const useStyles = makeStyles()((theme) => ({
    flexGrow: {
        flexGrow: 1,
    },
    button: {
        margin: theme.spacing(1),
    },
    language: {
        margin: theme.spacing(0, 0.5, 0, 1),
        display: 'none',
        [theme.breakpoints.up('md')]: {
            display: 'block',
        },
    },
    appName:{
        color: theme.palette.white,
        fontFamily: "Roboto",
        marginLeft: "20px",
        // Ce `<p>` brut garde sinon la marge par défaut du navigateur
        // (~1em haut/bas) : CssBaseline ne remet à zéro que les marges du
        // `body`, pas celles d'un `<p>` quelconque. Ça poussait la Toolbar
        // au-delà de sa hauteur standard (56/64px) sur tout le minimalLayout
        // — /login, /signup, /password/reset, et la collection publique.
        marginTop: 0,
        marginBottom: 0,
    },
}));

// ⚠️ « styled » et pas « withStyles » de tss-react : leurs signatures
// DIFFÈRENT — tss attend « withStyles(Composant, styles) » là où MUI v4
// attendait « withStyles(styles)(Composant) ». Le build passe dans les deux
// cas, Vite ne vérifiant pas les types : l'erreur ne serait apparue qu'à
// l'exécution, sur le seul bouton concerné.
//
// « styled » est natif à MUI, donc une dépendance de moins pour ce cas unique.
const LogoButton = styled(Button)(({theme}) => ({
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
    },
}));

const TopMenu = ({onSidebarOpen = () => {}, minimalLayout = true}) => {
    const {classes} = useStyles();
    const {t, i18n} = useTranslation('dashboard');
    const userLanguage = i18n.language
    const [languageMenu, setLanguageMenu] = React.useState(null);

    const handleLanguageIconClick = (event) => {
        setLanguageMenu(event.currentTarget);
    };

    // ⛔ Posait un témoin `userLanguage=noDefault` valable UN AN, que rien ne
    // relisait nulle part — retiré le 09/09/2026. Ne pas confondre avec la
    // variable `userLanguage` ci-dessus, qui vient d'i18n et sert à l'affichage :
    // même nom, deux choses sans rapport. C'est ce qui l'a fait survivre.
    // ⇒ L'application ne pose plus AUCUN témoin, et la politique de
    // confidentialité l'écrit. Toute ligne qui en reposerait un la rendrait
    // fausse.
    const handleLanguageMenuClose = () => {
        setLanguageMenu(null);
    };

    const handleLanguageChange = (languageCode) => {
        i18n.changeLanguage(languageCode)
        setLanguageMenu(null);
    }

    const languageButton = <>
        <Button
            color="inherit"
            aria-owns={languageMenu ? 'language-menu' : undefined}
            aria-haspopup="true"
            aria-label={t('changeLanguage')}
            onClick={handleLanguageIconClick}
            data-ga-event-category="header"
            data-ga-event-action="language"
        >
            <LanguageIcon/>
            <span className={classes.language}>
                {LANGUAGES_LABELS.filter((language) => language.code === userLanguage)[0]?.text}
              </span>
            <ExpandMoreIcon fontSize="small"/>
        </Button>
        <NoSsr defer>
            <Menu
                id="language-menu"
                anchorEl={languageMenu}
                open={Boolean(languageMenu)}
                onClose={handleLanguageMenuClose}
            >
                {LANGUAGES_LABELS.map((language) => (
                    <MenuItem
                        component="a"
                        data-no-link="true"
                        key={language.code}
                        selected={userLanguage === language.code}
                        onClick={() => handleLanguageChange(language.code)}
                        lang={language.code}
                        hrefLang={language.code}
                    >
                        {language.text}
                    </MenuItem>
                ))}
            </Menu>
        </NoSsr>
    </>

    const landingUrl = userLanguage === "fr" ? "https://www.labibli.com" : "https://www.labibli.com"

    let layout;
    if (minimalLayout) {
        layout = (
            <AppBar className={classes.root}>
                <Toolbar>
                    <a href={landingUrl} style={{textDecoration: 'none'}}>
                        <IconButton color="primary" size="large">
                            <Logo/>
                            <p className={classes.appName}>Labibli.com</p>
                        </IconButton>
                    </a>
                    <div className={classes.flexGrow}/>
                    {languageButton}
                </Toolbar>
                <Loading/>
            </AppBar>
        );
    } else {
        layout = (
            <AppBar className={classes.root}>
                <Toolbar>
                    <NavLink to={routes.default}>
                        <IconButton color="primary" size="large">
                            <Logo/>
                        </IconButton>

                    </NavLink>
                    <div className={classes.flexGrow}/>
                    {languageButton}
                    {/* ⛔ La déconnexion est descendue au bas du menu latéral,
                        comme sur Feuille de temps. Elle était ici sans libellé,
                        et enveloppée dans « Hidden xlDown » — un composant que
                        MUI v5 a supprimé, si bien que le bouton ne s'affichait
                        déjà plus. Personne ne l'a signalé, ce qui en dit assez
                        sur ce qu'une icône seule communique. */}
                    {/* Le bouton était masqué au-delà de xl, où le menu était
                        persistant. Le menu étant désormais escamotable partout,
                        le bouton doit exister partout, sinon il devient
                        inatteignable sur grand écran. */}
                    <LogoButton color="inherit" onClick={onSidebarOpen}>
                        <MenuIcon/>
                        {t('Menu')}
                    </LogoButton>
                </Toolbar>
                <Loading/>
            </AppBar>
        );
    }

    return layout;
};

TopMenu.propTypes = {
    minimalLayout: PropTypes.bool,
    onSidebarOpen: PropTypes.func,
};

export default TopMenu;
